import { Fragment, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, StatTile } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { isConstant, Nil, Table, TableFooter, THead, Th, Td, Tr } from '../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination } from '../../components/ui/Pagination';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { countryName, daysAgo, humanize, stripRawIds } from './format';
import { groupByCompany, type CompanyGroup } from './playGroups';
import { displayCompanyName } from './labels';
import { formatNumber } from '../../utils/numberFormat';
import { useAccess } from '../../hooks/useAccess';
import {
  useConfirmCandidate,
  useDismissCandidate,
  useEvaluatePlays,
  usePlaysOverview,
} from '../../hooks/usePlays';
import {
  APOLLO_LOOKUPS_PER_RUN,
  getPlayCandidatePage,
  PLAY_CANDIDATE_PAGE_SIZE,
  CANDIDATE_STATUS_LABELS,
  DOMAIN_SOURCE_LABELS,
  PERSON_SOURCE_LABELS,
  type CandidateStatus,
  type PlayCandidate,
  type PlayId,
  type PlayOverviewRow,
} from '../../api/plays';

/** Where a paused campaign is resumed. */
const LEMLIST_CAMPAIGNS_URL = 'https://app.lemlist.com/campaigns';

const STATUS_FILTERS: readonly TabItem<CandidateStatus>[] = [
  { id: 'proposed', label: 'Proposed' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'enrolled', label: 'Enrolled' },
  { id: 'dismissed', label: 'Dismissed' },
];

/**
 * Why a play with a detector has never proposed anyone: the source it reads is
 * empty. Said on the line so a zero is never left to guess (review #11).
 */
const EMPTY_SOURCE_REASONS: Partial<Record<PlayId, string>> = {
  signal_01_bid_desk_hiring:
    'No sourced company is hiring bid roles yet: the TheirStack pull is manual (Sourcing › Pull companies hiring)',
  signal_07_ai_for_bids_conversation:
    'Jungler has captured no post engagement yet (LinkedIn post engagement: no data on Signal model)',
  signal_06_read_our_material: 'RB2B has identified no visitor on our material pages yet',
};

/** One play as a flat row: the campaign it feeds, its state when it differs, and how full the queue is. */
function PlayLine({
  play,
  selected,
  onSelect,
  showStatus,
  showEnrolment,
}: {
  play: PlayOverviewRow;
  selected: boolean;
  onSelect: () => void;
  /** False when every play shares one campaign status; the tile says it once instead. */
  showStatus: boolean;
  /** False when no play has enrolled anyone or holds lemlist leads: "0 enrolled · 0 in lemlist" on every line says nothing. */
  showEnrolment: boolean;
}) {
  const campaignStatus = play.campaign?.status ?? null;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={play.campaign?.name ?? (play.ambiguous.length > 1 ? `Ambiguous: ${play.ambiguous.join(' / ')}` : 'No lemlist campaign with this prefix')}
        className={clsx(
          'flex w-full flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-left transition-colors',
          selected ? 'bg-surface-secondary' : 'hover:bg-surface-secondary/60',
        )}
      >
        <span className="min-w-[12rem] flex-1 text-body-sm font-medium text-text-primary">{play.label}</span>
        <span className="text-body-xs tabular-nums text-text-tertiary">
          {formatNumber(play.counts.proposed)} proposed
          {showEnrolment && (
            <>
              {' '}
              · {formatNumber(play.counts.enrolled)} enrolled · {formatNumber(play.leadsInCampaign)} in lemlist
            </>
          )}
          {play.counts.blocked > 0 && <span className="text-warning-dark"> · {play.counts.blocked} blocked</span>}
        </span>
        {!play.campaign ? (
          <Badge variant="error">No campaign</Badge>
        ) : showStatus && campaignStatus !== 'running' ? (
          // Running is the healthy state and shows nothing; paused or unknown is the exception.
          <Badge>{humanize(campaignStatus) || 'Unknown'}</Badge>
        ) : null}
        {play.detectorGap ? (
          <span className="basis-full text-body-xs text-text-tertiary">
            <span className="text-warning-dark">No detector yet:</span> {play.detectorGap.replace(/\s+—\s+/g, '; ')}
          </span>
        ) : Object.values(play.counts).every((n) => n === 0) && EMPTY_SOURCE_REASONS[play.playId] ? (
          <span className="basis-full text-body-xs text-text-tertiary">{EMPTY_SOURCE_REASONS[play.playId]}</span>
        ) : null}
      </button>
    </li>
  );
}

/** "12d ago" → "12 days ago", for a sentence. */
function seenPhrase(iso: string): string {
  return daysAgo(iso).replace(/^(\d+)d ago$/, (_, n: string) => `${n} days ago`);
}

function personSourceLabel(source: string | null): string | null {
  return source ? PERSON_SOURCE_LABELS[source] ?? humanize(source) : null;
}

/** The person source most rows share (half or more), so it is said once in the header instead of on every row. */
function commonPersonSource(rows: PlayCandidate[]): string | null {
  const named = rows.filter((r) => r.personSource && !r.needsContact && r.personName);
  if (named.length < 2) return null;
  const counts = new Map<string, number>();
  for (const r of named) counts.set(r.personSource!, (counts.get(r.personSource!) ?? 0) + 1);
  const [source, n] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]!;
  return n / named.length >= 0.5 ? source : null;
}

function PersonCell({ row, commonSource }: { row: PlayCandidate; commonSource: string | null }) {
  if (row.needsContact || !row.personName) {
    const leads = row.evidence?.apolloLeads ?? [];
    return (
      <div className="space-y-0.5">
        <span title="Research team: add someone to the leads sheet's Contacts tab">
          <Badge variant="warning">Needs a contact</Badge>
        </span>
        {leads.length > 0 && (
          <p
            className="cursor-help text-body-xs text-text-tertiary"
            title={`Apollo names (no LinkedIn URL without a paid reveal):\n${leads
              .map((l) => `${l.name}${l.title ? `, ${l.title}` : ''}`)
              .join('\n')}`}
          >
            {leads.length} Apollo name{leads.length === 1 ? '' : 's'}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="text-text-primary">
        {row.personLinkedinUrl ? (
          <a href={row.personLinkedinUrl} target="_blank" rel="noreferrer" className="hover:underline">
            {row.personName}
          </a>
        ) : (
          row.personName
        )}
      </p>
      <p className="text-body-xs text-text-tertiary">
        {[
          row.personTitle,
          row.personSource !== commonSource ? personSourceLabel(row.personSource) : null,
          row.personLinkedinUrl ? null : 'no LinkedIn URL',
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </div>
  );
}

/**
 * The lemlist variables this candidate would be enrolled with. A required one
 * that is empty is flagged: confirming would be blocked for exactly that reason.
 */
function CopyVariables({ row }: { row: PlayCandidate }) {
  const values = row.evidence?.copyVariables ?? {};
  const missing = row.evidence?.missingCopyVariables ?? [];
  const entries = Object.entries(values);
  if (entries.length === 0 && missing.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {entries.map(([name, value]) => (
        <span
          key={name}
          className="max-w-full truncate rounded bg-surface-secondary px-1.5 py-0.5 text-body-xs text-text-secondary"
          title={`{{${name}}} = ${value}`}
        >
          <span className="text-text-tertiary">{humanize(name)}:</span> {value}
        </span>
      ))}
      {missing.map((name) => (
        <Badge key={name} variant="error">
          {humanize(name)} missing
        </Badge>
      ))}
    </div>
  );
}

function hasCopy(row: PlayCandidate): boolean {
  return (
    Object.keys(row.evidence?.copyVariables ?? {}).length > 0 || (row.evidence?.missingCopyVariables ?? []).length > 0
  );
}

/** Older than this, the queue is out of date: proposals reflect signals from before the last evaluation. */
const STALE_EVALUATION_DAYS = 2;

/** Sources whose stored `country` is where the person sits (contacts tab, RB2B, Jungler). */
const PERSON_LOCATED_SOURCES = new Set(['contacts_tab', 'rb2b', 'jungler']);

/** The person's own location; candidates evaluated before the split fall back by source. */
function personLocation(row: PlayCandidate): string | null {
  if (row.needsContact || !row.personName) return null;
  if (row.evidence && 'personCountry' in row.evidence) return row.evidence.personCountry ?? null;
  return row.personSource && PERSON_LOCATED_SOURCES.has(row.personSource) ? row.country : null;
}

/** The company's HQ country; older candidates only know it when no person (or an Attio/Apollo person) was attached. */
function companyHq(row: PlayCandidate): string | null {
  if (row.evidence && 'companyCountry' in row.evidence) return row.evidence.companyCountry ?? null;
  if (row.needsContact || !row.personName) return row.country;
  return row.personSource && !PERSON_LOCATED_SOURCES.has(row.personSource) ? row.country : null;
}

function GroupRows({
  group,
  isAdmin,
  commonSource,
  showSeen,
  playLabel,
  campaignPaused,
  allPaused,
  onConfirm,
  onDismiss,
  busy,
  open,
  onToggle,
}: {
  group: CompanyGroup;
  isAdmin: boolean;
  commonSource: string | null;
  /** False when every row was seen the same day; the header says it once instead. */
  showSeen: boolean;
  /** The play this row belongs to, shown when the list spans plays; null when one play is selected. */
  playLabel: string | null;
  /** The play's lemlist campaign is paused: enrolling would queue leads that send nothing. */
  campaignPaused: boolean;
  /** Every campaign is paused: the header banner says so once, rows stay quiet. */
  allPaused: boolean;
  onConfirm: (row: PlayCandidate) => void;
  onDismiss: (rows: PlayCandidate[]) => void;
  busy: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const { first, lines } = group;
  const span = lines.length;
  const hq = companyHq(first);
  const domain = first.companyDomain
    ? first.evidence?.domainSource && first.evidence.domainSource !== 'sourcing'
      ? `${first.companyDomain} (${DOMAIN_SOURCE_LABELS[first.evidence.domainSource] ?? first.evidence.domainSource})`
      : first.companyDomain
    : null;
  return (
    <>
      {lines.map((line, i) => {
        const row = line.primary;
        const all = [row, ...line.dupes];
        const canConfirm = isAdmin && (row.status === 'proposed' || row.status === 'blocked') && !row.needsContact;
        const canDismiss = isAdmin && row.status !== 'enrolled' && row.status !== 'dismissed';
        const location = personLocation(row);
        return (
          <Tr key={row.id} onClick={onToggle} className="group">
            {i === 0 && (
              <Td rowSpan={span} className="align-top">
                <div className="space-y-0.5">
                  {playLabel && <Badge>{playLabel}</Badge>}
                  <p className="text-text-primary" title={first.companyName !== group.names[0] ? first.companyName : undefined}>
                    {group.names[0]}
                  </p>
                  <p className="text-body-xs text-text-tertiary">
                    {[domain, hq ? `HQ ${countryName(hq)}` : null].filter(Boolean).join(' · ')}
                  </p>
                  {group.names.length > 1 && (
                    <p className="text-body-xs text-text-tertiary" title={group.names.slice(1).join('\n')}>
                      Also proposed as {group.names.slice(1, 3).join(', ')}
                      {group.names.length > 3 ? ` +${group.names.length - 3}` : ''}
                    </p>
                  )}
                </div>
              </Td>
            )}
            <Td>
              <PersonCell row={row} commonSource={commonSource} />
            </Td>
            <Td className="text-body-xs text-text-secondary">{location ? countryName(location) : <Nil />}</Td>
            {i === 0 && (
              <Td rowSpan={span} className="align-top">
                <div className="flex items-start gap-1">
                  <p className={clsx('min-w-0 text-body-xs text-text-secondary', !open && 'truncate')}>{stripRawIds(first.evidenceSummary)}</p>
                  <ChevronRight
                    className={clsx('mt-0.5 h-3.5 w-3.5 flex-none text-text-muted transition-transform', open && 'rotate-90')}
                    aria-hidden
                  />
                </div>
                {first.status === 'enrolled' && first.lemlistCampaignName && (
                  <p className="mt-0.5 text-body-xs text-text-tertiary">In {first.lemlistCampaignName}</p>
                )}
                {open && <CopyVariables row={first} />}
                {open && !hasCopy(first) && (
                  <p className="mt-1 text-body-xs text-text-muted">No copy variables recorded for this candidate yet.</p>
                )}
              </Td>
            )}
            {showSeen && i === 0 && (
              <Td
                rowSpan={span}
                className="whitespace-nowrap align-top text-body-xs text-text-tertiary"
                title={`Newest signal behind this row: ${formatDayMonthYear(group.lastSeenAt)}`}
              >
                {daysAgo(group.lastSeenAt)}
              </Td>
            )}
            <Td align="right">
              {row.blockedReason && <p className="mb-0.5 text-left text-body-xs text-warning-dark">{row.blockedReason}</p>}
              {(canConfirm || canDismiss) && (
                <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {canConfirm && campaignPaused && allPaused && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      title="Every lemlist campaign is paused, so enrolling is off until they are resumed."
                      aria-label={`Enrol ${row.personName ?? row.companyName} in lemlist (off: campaigns paused)`}
                    >
                      Enrol
                    </Button>
                  )}
                  {canConfirm && campaignPaused && !allPaused && (
                    <a
                      href={LEMLIST_CAMPAIGNS_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center whitespace-nowrap rounded-md border border-border-light px-2 py-1 text-body-xs text-warning-dark hover:underline"
                      title="This play's lemlist campaign is paused, so nobody can be enrolled. Resume it in lemlist first."
                    >
                      Campaign paused
                    </a>
                  )}
                  {canConfirm && !campaignPaused && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConfirm(row)}
                      disabled={busy}
                      title="Enrol this person in the lemlist campaign for this play (the outbound checks run first)"
                      aria-label={`Enrol ${row.personName ?? row.companyName} in lemlist`}
                    >
                      Enrol
                    </Button>
                  )}
                  {canDismiss && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDismiss(all)}
                      disabled={busy}
                      title={
                        all.length > 1
                          ? `Dismiss: remove all ${all.length} proposals of this person from the queue`
                          : 'Dismiss: remove this proposal from the queue (kept, not deleted)'
                      }
                      aria-label={`Dismiss ${row.personName ?? row.companyName}`}
                    >
                      Dismiss
                    </Button>
                  )}
                </div>
              )}
            </Td>
          </Tr>
        );
      })}
    </>
  );
}

/**
 * GTM → Plays.
 *
 * The queue between a signal and a lemlist campaign. Eight plays, one per
 * "[SIGNAL 0x]" campaign (04 split into 04A defend / 04B challenge): the
 * evaluator proposes (company, person) pairs with the evidence and the copy
 * variables they would be sent with; a human confirms or dismisses; confirming runs the outbound
 * guardrails and enrols the person through the lemlist API. Nobody is enrolled
 * without the click, and a refusal is shown on the row, never hidden.
 */
export function PlaysView() {
  const { isAdmin } = useAccess();
  const [playId, setPlayId] = useState<PlayId | undefined>(undefined);
  const [status, setStatus] = useState<CandidateStatus>('proposed');

  const overview = usePlaysOverview();
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [playId, status]);
  // Paged by company on the server; the ['gtm-plays', …] key keeps the mutations' invalidation.
  const candidates = useQuery({
    queryKey: ['gtm-plays', 'candidates', 'page', { playId, status, page }],
    queryFn: () => getPlayCandidatePage({ playId, statuses: [status], page }),
    placeholderData: keepPreviousData,
  });
  const evaluate = useEvaluatePlays();
  const confirm = useConfirmCandidate();
  const dismiss = useDismissCandidate();
  const [lastOutcome, setLastOutcome] = useState<{ ok: boolean; text: string } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const plays = overview.data?.plays ?? [];
  // Rows grouped by play, in the order the play cards show; the API's order is kept inside a play.
  const loadedRows = candidates.data?.rows ?? [];
  // Persona false positives ("Bar Tender") stay stored but are not shown; the footer counts them.
  // The API leaves them out before paging and says how many across every page; an older
  // build flags them on the page instead, so the count is this page's.
  const serverPersonaHidden = candidates.data?.personaHidden;
  const personaHidden = serverPersonaHidden ?? loadedRows.filter((r) => r.personaExcluded).length;
  const candidateRows = useMemo(() => {
    const order = new Map((overview.data?.plays ?? []).map((p, i) => [p.playId, i]));
    return (candidates.data?.rows ?? [])
      .filter((r) => !r.personaExcluded)
      .sort((a, b) => (order.get(a.playId) ?? 99) - (order.get(b.playId) ?? 99));
  }, [candidates.data, overview.data]);
  // One entry per company per play, so a company and its people read as one block.
  const companyGroups = useMemo(() => groupByCompany(candidateRows), [candidateRows]);
  const companyTotal = candidates.data?.companies ?? 0;
  const pageCount = Math.max(1, Math.ceil(companyTotal / PLAY_CANDIDATE_PAGE_SIZE));
  // Confirming or dismissing the last companies of the last page: step back instead of showing an empty page.
  useEffect(() => {
    if (candidates.data && !candidates.isPlaceholderData && page > pageCount) setPage(pageCount);
  }, [candidates.data, candidates.isPlaceholderData, page, pageCount]);
  const matched = candidates.data?.total ?? candidateRows.length;
  const commonSource = commonPersonSource(candidateRows);
  // "8d ago" on every row says nothing per row: drop the column and say it once in the header.
  const sameSeen = candidateRows.length > 1 && isConstant(candidateRows, (r) => daysAgo(r.lastSeenAt));
  const showSeen = !sameSeen;
  // One play selected (or only one present): the card header already names it.
  const anyCopy = candidateRows.some(hasCopy);
  const lastEvaluatedAt = overview.data?.lastEvaluatedAt ?? null;
  const evaluationAgeDays = lastEvaluatedAt ? (Date.now() - new Date(lastEvaluatedAt).getTime()) / 86_400_000 : null;
  const staleEvaluation = evaluationAgeDays !== null && evaluationAgeDays > STALE_EVALUATION_DAYS;
  const totals = plays.reduce(
    (acc, p) => ({ proposed: acc.proposed + p.counts.proposed, enrolled: acc.enrolled + p.counts.enrolled }),
    { proposed: 0, enrolled: 0 },
  );
  const pausedPlays = plays.filter((p) => p.campaign?.status === 'paused').length;
  const showEnrolment = totals.enrolled > 0 || plays.some((p) => p.leadsInCampaign > 0);
  const noCampaign = plays.filter((p) => !p.campaign).length;

  const busy = confirm.isPending || dismiss.isPending;

  // A campaign status every play shares is said once in the header, not on each card.
  const statuses = new Set(plays.map((p) => (p.campaign ? p.campaign.status ?? '' : null)));
  const sharedStatus =
    plays.length > 1 && statuses.size === 1 && !statuses.has(null) && !statuses.has('') ? [...statuses][0] : null;

  const onConfirm = (row: PlayCandidate) => {
    const ok = window.confirm(
      `Enrol ${row.personName ?? 'this person'} (${displayCompanyName(row.companyName)}) in the lemlist campaign for this play?\n\nThe outbound guardrails run first; lemlist is only called if they pass.`,
    );
    if (!ok) return;
    confirm.mutate(row.id, {
      onSuccess: (result) => {
        setLastOutcome(
          result.outcome === 'enrolled'
            ? { ok: true, text: `Enrolled ${row.personName} in ${result.candidate.lemlistCampaignName ?? 'the campaign'}.` }
            : { ok: false, text: `Not enrolled: ${result.reason ?? 'blocked'}` },
        );
      },
      onError: (error) => {
        setLastOutcome({ ok: false, text: error instanceof Error ? error.message : 'Confirm failed' });
      },
    });
  };

  /** Dismiss a person and every duplicate proposal of them under another entity name. */
  const onDismiss = (rows: PlayCandidate[]) => {
    const row = rows[0]!;
    const note = window.prompt(
      `Dismiss ${row.personName ?? displayCompanyName(row.companyName)}${
        rows.length > 1 ? ` (${rows.length} proposals under different entity names)` : ''
      }? Optional note:`,
      '',
    );
    if (note === null) return;
    for (const r of rows) {
      dismiss.mutate(
        { id: r.id, ...(note ? { note } : {}) },
        {
          onError: (error) => {
            setLastOutcome({ ok: false, text: error instanceof Error ? error.message : 'Dismiss failed' });
          },
        },
      );
    }
  };

  return (
    <div className="space-y-5">
      {overview.data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile
            label="Proposed"
            value={formatNumber(totals.proposed)}
            sub={`Waiting for a decision · all ${plays.length} plays${
              lastEvaluatedAt
                ? ` · evaluated ${daysAgo(lastEvaluatedAt)}${staleEvaluation ? ', out of date' : ''}`
                : ' · never evaluated'
            }`}
            tooltip={
              lastEvaluatedAt
                ? `Last evaluated ${formatDayMonthYear(lastEvaluatedAt)}. The daily Signal Ops run re-evaluates the plays; older than ${STALE_EVALUATION_DAYS} days means that run has not reached them.`
                : undefined
            }
          />
          <StatTile
            label="Enrolled"
            value={formatNumber(totals.enrolled)}
            sub="Put in lemlist from a play · all time"
          />
          <StatTile
            label="Paused plays"
            value={`${pausedPlays} of ${plays.length}`}
            sub={`lemlist campaign status now${noCampaign > 0 ? ` · ${noCampaign} with no campaign` : ''}${
              pausedPlays > 0 ? ' · a paused campaign sends nothing until resumed' : ''
            } · click to open lemlist`}
            onClick={() => window.open(LEMLIST_CAMPAIGNS_URL, '_blank', 'noopener,noreferrer')}
            // The one exception on this tab: a paused campaign sends nothing, whatever is confirmed.
            icon={pausedPlays + noCampaign > 0 ? <AlertTriangle className="h-5 w-5 text-warning-dark" aria-label="Needs attention" /> : undefined}
          />
        </div>
      )}

      <Card padding="sm" className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-body-md font-medium text-text-primary">Signal campaigns</h3>
            {sharedStatus && sharedStatus !== 'running' && sharedStatus !== 'paused' && (
              <span className="text-body-xs text-text-tertiary">
                All {plays.length} campaigns {humanize(sharedStatus).toLowerCase()}
              </span>
            )}
            <InfoTip>
              Each play feeds one “[SIGNAL 0x]” lemlist campaign. Evaluating re-reads the detectors (sourcing, the
              leads sheet’s Contacts tab, RB2B, Jungler) and refreshes the proposals. Nobody is enrolled until a row is
              confirmed; confirming runs the do-not-contact, country and one-sequence checks, then calls lemlist.
            </InfoTip>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={() => evaluate.mutate({})} disabled={evaluate.isPending}>
                {evaluate.isPending && !evaluate.variables?.apollo ? 'Evaluating…' : 'Evaluate signals'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const ok = window.confirm(
                    `Also look up companies with no contact on Apollo? Apollo's organisation search consumes credits: up to ${APOLLO_LOOKUPS_PER_RUN} companies this run; companies looked up in the last 30 days are skipped.`,
                  );
                  if (ok) evaluate.mutate({ apollo: true });
                }}
                disabled={evaluate.isPending}
              >
                {evaluate.isPending && evaluate.variables?.apollo
                  ? 'Evaluating with Apollo…'
                  : `Evaluate + Apollo (${APOLLO_LOOKUPS_PER_RUN} credits)`}
              </Button>
            </div>
          )}
        </div>

        {sharedStatus === 'paused' && (
          <Alert variant="warning">
            All {plays.length} campaigns are paused in lemlist: enrol is off until you resume them.{' '}
            <a href={LEMLIST_CAMPAIGNS_URL} target="_blank" rel="noreferrer" className="text-accent hover:underline">
              Open lemlist
            </a>
          </Alert>
        )}

        {staleEvaluation && lastEvaluatedAt && (
          <p className="text-body-xs text-warning-dark" title={formatDayMonthYear(lastEvaluatedAt)}>
            Last evaluated {Math.floor(evaluationAgeDays!)} days ago: proposals miss any signal since then.
            {isAdmin ? ' Evaluate signals to refresh them.' : ''}
          </p>
        )}
        {evaluate.isError && <ErrorState title="Evaluation failed" error={evaluate.error} compact />}
        {evaluate.data && (
          <Alert variant={evaluate.data.warnings.length > 0 ? 'warning' : 'success'}>
            <div className="space-y-1">
              <p>
                Evaluated {evaluate.data.reports.filter((r) => !r.notRun).length} detectors ·{' '}
                {evaluate.data.reports.reduce((n, r) => n + r.proposed, 0)} candidates written or refreshed ·{' '}
                {evaluate.data.contactsTabRows} people on the Contacts tab.
              </p>
              {evaluate.data.reports
                .filter(
                  (r) =>
                    r.skippedWhy.length > 0 ||
                    r.withoutContact > 0 ||
                    (r.apollo?.lookedUp ?? 0) > 0 ||
                    (r.apollo?.notRequested ?? 0) > 0,
                )
                .map((r) => (
                  <p key={r.playId} className="text-body-xs">
                    {overview.data?.plays.find((p) => p.playId === r.playId)?.label ?? r.playId}:{' '}
                    {[
                      r.withoutContact > 0 ? `${r.withoutContact} companies without a contact` : null,
                      r.apollo && r.apollo.lookedUp > 0
                        ? `Apollo: ${r.apollo.lookedUp} looked up, ${r.apollo.found} with named people, ${r.apollo.withLinkedin} enrollable${
                            r.apollo.errors.length > 0 ? `, ${r.apollo.errors.length} failed` : ''
                          }`
                        : r.apollo && !r.apollo.requested && r.apollo.notRequested > 0
                          ? 'Apollo skipped: not requested for this run'
                          : null,
                      ...r.skippedWhy,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                ))}
              {evaluate.data.warnings.map((w) => (
                <p key={w} className="text-body-xs">
                  {w}
                </p>
              ))}
            </div>
          </Alert>
        )}
        {lastOutcome && (
          <Alert variant={lastOutcome.ok ? 'success' : 'warning'} dismissible onDismiss={() => setLastOutcome(null)}>
            {lastOutcome.text}
          </Alert>
        )}

        {overview.isLoading ? (
          <LoadingState />
        ) : overview.isError ? (
          <ErrorState title="Couldn’t load the plays" error={overview.error} onRetry={() => overview.refetch()} />
        ) : (
          <ul className="-mx-3 divide-y divide-border-light border-y border-border-light">
            {plays.map((play) => (
              <PlayLine
                key={play.playId}
                play={play}
                showStatus={!sharedStatus}
                showEnrolment={showEnrolment}
                selected={playId === play.playId}
                onSelect={() => setPlayId(playId === play.playId ? undefined : play.playId)}
              />
            ))}
          </ul>
        )}
      </Card>

      <Card padding="sm">
        <CardHeader
          title={playId ? overview.data?.plays.find((p) => p.playId === playId)?.label ?? 'Candidates' : 'All plays'}
          description={
            <span className="inline-flex flex-wrap items-center gap-1">
              {[
                `${formatNumber(matched)} ${CANDIDATE_STATUS_LABELS[status].toLowerCase()} candidate${matched === 1 ? '' : 's'} at ${formatNumber(
                  companyTotal,
                )} compan${companyTotal === 1 ? 'y' : 'ies'}`,
                commonSource ? `people from ${personSourceLabel(commonSource)} unless shown` : null,
                // "seen" is the date of the signal behind each row, not when the play
                // was evaluated (that is the "evaluated …" line above).
                sameSeen ? `newest signal on every row ${seenPhrase(candidateRows[0]!.lastSeenAt)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              <InfoTip label="How the queue is ordered">
                One block per company per play: entity names of one company (“CGI”, “CGI FRANCE”) and a person proposed under
                several names show once. Plays come in the order above, then the strongest signal first (tenders won, re-tender points, visits), then companies with a named person, then the freshest signal. Click a row for
                the full reason{anyCopy ? ' and the copy it would be sent with' : ''}.
              </InfoTip>
            </span>
          }
          action={<SegmentedControl options={STATUS_FILTERS} value={status} onChange={setStatus} ariaLabel="Status" />}
        />

        {candidates.isLoading ? (
          <LoadingState rows={5} />
        ) : candidates.isError ? (
          <ErrorState title="Couldn’t load candidates" error={candidates.error} onRetry={() => candidates.refetch()} />
        ) : candidateRows.length === 0 ? (
          <EmptyState
            title={status === 'proposed' ? 'Nothing proposed' : `No ${CANDIDATE_STATUS_LABELS[status].toLowerCase()} candidates`}
            description={status === 'proposed' ? 'Proposals appear when the detectors are next evaluated.' : undefined}
            compact
          />
        ) : (
          <div className={candidates.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined} aria-busy={candidates.isPlaceholderData}>
          <Table
            minWidth={showSeen ? 1100 : 1020}
            colWidths={
              showSeen
                ? ['22%', '20%', '8rem', 'auto', '4.5rem', '11rem']
                : ['22%', '20%', '8rem', 'auto', '11rem']
            }
          >
            <THead>
              <tr>
                <Th>Company</Th>
                <Th>Person</Th>
                <Th>
                  <span className="inline-flex items-center gap-1">
                    Location
                    <InfoTip>
                      Where the person sits, from the Contacts tab, RB2B or Jungler. The company’s own country is the “HQ”
                      under its name; Attio and Apollo give no personal location, so those rows show a dot.
                    </InfoTip>
                  </span>
                </Th>
                <Th>Why</Th>
                {showSeen && <Th>Signal</Th>}
                <Th align="right">Action</Th>
              </tr>
            </THead>
            <tbody>
              {companyGroups.map((group) => {
                const play = plays.find((p) => p.playId === group.playId);
                return (
                  <Fragment key={group.id}>
                    <GroupRows
                      group={group}
                      playLabel={playId ? null : play?.label ?? humanize(group.playId)}
                      isAdmin={isAdmin}
                      commonSource={commonSource}
                      showSeen={showSeen}
                      campaignPaused={play?.campaign?.status === 'paused'}
                      allPaused={sharedStatus === 'paused'}
                      busy={busy}
                      onConfirm={onConfirm}
                      onDismiss={onDismiss}
                      open={openId === group.id}
                      onToggle={() => setOpenId((cur) => (cur === group.id ? null : group.id))}
                    />
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          {personaHidden > 0 && (
            <TableFooter>
              {formatNumber(personaHidden)} {serverPersonaHidden !== undefined ? 'more' : 'on this page'} not shown: the title is
              not a bid-team role (e.g. “Bar Tender”, pre-sales without bid work); kept, not deleted
            </TableFooter>
          )}
          <Pagination
            page={page}
            pageCount={pageCount}
            total={companyTotal}
            pageSize={PLAY_CANDIDATE_PAGE_SIZE}
            onPage={setPage}
          />
          </div>
        )}
      </Card>
    </div>
  );
}
